import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Plus, Trash2, IndianRupee } from "lucide-react";
import { api } from "@/lib/api";
import { DARK_DIALOG, DARK_INPUT, MfaField, fmtDate, errMsg, inr } from "./shared";

const Num = ({ value, onChange, testid, suffix, w = "w-full" }) => (
  <div className={`relative ${w}`}>
    <Input type="number" value={value} onChange={(e) => onChange(e.target.value)} className={`${DARK_INPUT} font-mono pr-8`} data-testid={testid}/>
    {suffix && <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-slate-500">{suffix}</span>}
  </div>
);

const hasBlank = (v) => [...Object.values(v.rates), v.platform_fee_pct, v.gst_pct, v.min_booking_amount, v.advance_amount_default,
  ...v.cancellation_tiers.flatMap((t) => [t.min_hours_before, t.fee_pct])].some((x) => x === "" || x === null || Number.isNaN(Number(x)));

export default function PricingPage() {
  const [cfg, setCfg] = useState(null);
  const [v, setV] = useState(null);
  const [confirm, setConfirm] = useState(false);
  const load = () => api.get("/admin/pricing-config").then((r) => { setCfg(r.data); setV(JSON.parse(JSON.stringify(r.data))); }).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { load(); }, []);
  if (!v) return <div className="text-slate-400">Loading…</div>;
  const set = (k) => (x) => setV({ ...v, [k]: x });
  const setTier = (i, k, x) => setV({ ...v, cancellation_tiers: v.cancellation_tiers.map((t, j) => (j === i ? { ...t, [k]: x } : t)) });
  const dirty = JSON.stringify(v) !== JSON.stringify(cfg);

  return (
    <div data-testid="pricing-page" className="max-w-5xl">
      <div className="text-[10px] uppercase tracking-[0.25em] text-orange-400 font-bold">Super Admin · Pricing</div>
      <h1 className="font-display text-3xl font-bold mt-1">Pricing & Fees</h1>
      <p className="text-sm text-slate-400 mt-1 mb-6">Changes apply to <b className="text-slate-200">new bookings only</b>. Existing bookings keep the price and cancellation policy they were created with. Current version <span className="font-mono text-orange-300" data-testid="pricing-version">v{cfg.pricing_version}</span>.</p>

      <div className="grid lg:grid-cols-2 gap-5">
        <section className="admin-card p-5" data-testid="pricing-rates-card">
          <div className="font-bold mb-3 flex items-center gap-2"><IndianRupee size={15} className="text-orange-400"/>Service rates (per worker per day)</div>
          <div className="space-y-3">
            {Object.entries(v.rates).map(([k, r]) => (
              <div key={k} className="grid grid-cols-[1fr_140px] items-center gap-3">
                <label className="text-sm text-slate-300">{v.labels[k]}</label>
                <Num value={r} onChange={(x) => setV({ ...v, rates: { ...v.rates, [k]: x } })} testid={`pricing-rate-${k}`} suffix="₹"/>
              </div>
            ))}
          </div>
        </section>
        <section className="admin-card p-5" data-testid="pricing-fees-card">
          <div className="font-bold mb-3">Fees & tax</div>
          <div className="grid grid-cols-2 gap-3 text-xs">
            <div><label className="text-slate-400 font-semibold">Platform fee</label><Num value={v.platform_fee_pct} onChange={set("platform_fee_pct")} testid="pricing-platform-fee" suffix="%"/></div>
            <div><label className="text-slate-400 font-semibold">GST</label><Num value={v.gst_pct} onChange={set("gst_pct")} testid="pricing-gst" suffix="%"/></div>
            <div><label className="text-slate-400 font-semibold">Minimum booking</label><Num value={v.min_booking_amount} onChange={set("min_booking_amount")} testid="pricing-min-booking" suffix="₹"/></div>
            <div><label className="text-slate-400 font-semibold">Advance payment</label><Num value={v.advance_amount_default} onChange={set("advance_amount_default")} testid="pricing-advance" suffix="₹"/></div>
          </div>
          <Example v={v}/>
        </section>
      </div>

      <CancellationTiers tiers={v.cancellation_tiers} setTier={setTier} onAdd={() => setV({ ...v, cancellation_tiers: [...v.cancellation_tiers, { min_hours_before: 48, fee_pct: 0 }] })}
        onRemove={(i) => setV({ ...v, cancellation_tiers: v.cancellation_tiers.filter((_, j) => j !== i) })}/>

      <div className="flex gap-3 mt-5">
        <Button className="bg-orange-500 hover:bg-orange-600" disabled={!dirty} onClick={() => (hasBlank(v) ? toast.error("Fill in every field before publishing") : setConfirm(true))} data-testid="pricing-save-btn">Publish new pricing</Button>
        {dirty && <Button variant="ghost" className="text-slate-400" onClick={() => setV(JSON.parse(JSON.stringify(cfg)))} data-testid="pricing-reset-btn">Discard changes</Button>}
      </div>
      <History items={cfg.history}/>
      {confirm && <ConfirmDialog v={v} onClose={() => setConfirm(false)} onDone={() => { setConfirm(false); load(); }}/>}
    </div>
  );
}

function Example({ v }) {
  const labour = Number(Object.values(v.rates)[0] || 0) * 2;
  const fee = Math.round(labour * Number(v.platform_fee_pct || 0) / 100);
  const gst = Math.round((labour + fee) * Number(v.gst_pct || 0) / 100);
  return (
    <div className="mt-4 rounded-lg border border-slate-800 bg-slate-950/60 p-3 text-xs font-mono space-y-1" data-testid="pricing-example">
      <div className="text-slate-500 font-sans">Example: {Object.values(v.labels)[0]}, 2 workers, 1 day</div>
      <div className="flex justify-between"><span>Labour</span><span>{inr(labour)}</span></div>
      <div className="flex justify-between"><span>Platform fee</span><span>{inr(fee)}</span></div>
      <div className="flex justify-between"><span>GST</span><span>{inr(gst)}</span></div>
      <div className="flex justify-between text-orange-300 font-bold border-t border-slate-800 pt-1"><span>Total</span><span data-testid="pricing-example-total">{inr(labour + fee + gst)}</span></div>
    </div>
  );
}

function CancellationTiers({ tiers, setTier, onAdd, onRemove }) {
  return (
    <section className="admin-card p-5 mt-5" data-testid="pricing-cancellation-card">
      <div className="flex items-center justify-between mb-1">
        <div className="font-bold">Cancellation charges</div>
        <Button size="sm" variant="outline" className="border-slate-700 bg-transparent text-slate-300 h-8" onClick={onAdd} disabled={tiers.length >= 6} data-testid="pricing-add-tier"><Plus size={13} className="mr-1"/>Add tier</Button>
      </div>
      <p className="text-xs text-slate-500 mb-4">Fee = % of booking total, based on how long before the scheduled start the customer cancels. Include a 0-hour tier.</p>
      <div className="space-y-2">
        {tiers.map((t, i) => (
          <div key={i} className="flex flex-wrap items-center gap-3 text-sm" data-testid={`pricing-tier-${i}`}>
            <span className="text-slate-400 text-xs w-28">Cancelled ≥</span>
            <Num value={t.min_hours_before} onChange={(x) => setTier(i, "min_hours_before", x)} testid={`pricing-tier-hours-${i}`} suffix="h" w="w-28"/>
            <span className="text-slate-400 text-xs">before start → fee</span>
            <Num value={t.fee_pct} onChange={(x) => setTier(i, "fee_pct", x)} testid={`pricing-tier-fee-${i}`} suffix="%" w="w-28"/>
            <Button size="sm" variant="ghost" className="text-slate-500 hover:text-red-300 h-8" onClick={() => onRemove(i)} disabled={tiers.length <= 1} data-testid={`pricing-tier-remove-${i}`}><Trash2 size={13}/></Button>
          </div>
        ))}
      </div>
    </section>
  );
}

function History({ items }) {
  if (!items?.length) return null;
  return (
    <section className="admin-card p-5 mt-5" data-testid="pricing-history">
      <div className="font-bold mb-3">Change history</div>
      <div className="divide-y divide-slate-800 text-xs">
        {items.map((h) => (
          <div key={h.id} className="py-2 flex flex-wrap gap-x-4">
            <span className="font-mono text-orange-300">v{h.version}</span><span className="text-slate-500">{fmtDate(h.at)}</span>
            <span className="text-slate-300">{Object.keys(h.changes).join(", ") || "no changes"}</span>{h.note && <span className="text-slate-500">“{h.note}”</span>}
          </div>
        ))}
      </div>
    </section>
  );
}

function ConfirmDialog({ v, onClose, onDone }) {
  const [mfa, setMfa] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const go = async () => {
    setBusy(true);
    try {
      const n = (x) => Number(x);
      await api.put("/admin/pricing-config", {
        rates: Object.fromEntries(Object.entries(v.rates).map(([k, r]) => [k, parseInt(r, 10)])),
        platform_fee_pct: n(v.platform_fee_pct), gst_pct: n(v.gst_pct), min_booking_amount: parseInt(v.min_booking_amount, 10),
        advance_amount_default: parseInt(v.advance_amount_default, 10),
        cancellation_tiers: v.cancellation_tiers.map((t) => ({ min_hours_before: n(t.min_hours_before), fee_pct: n(t.fee_pct) })),
        mfa_code: mfa, note,
      });
      toast.success("New pricing published for new bookings"); onDone();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className={`${DARK_DIALOG} max-w-sm`} data-testid="pricing-confirm-dialog">
        <DialogHeader><DialogTitle>Publish new pricing?</DialogTitle><DialogDescription className="text-slate-400">Applies to bookings created from now on. Existing bookings are unchanged.</DialogDescription></DialogHeader>
        <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Reason (optional)" className={DARK_INPUT} data-testid="pricing-note"/>
        <MfaField value={mfa} onChange={setMfa} testid="pricing-mfa"/>
        <div className="flex justify-end"><Button className="bg-orange-500 hover:bg-orange-600" disabled={mfa.length !== 6 || busy} onClick={go} data-testid="pricing-confirm-btn">Publish</Button></div>
      </DialogContent>
    </Dialog>
  );
}
